import {
  createMachine,
  assign,
  assertEvent,
  fromPromise,
} from 'xstate';
import Uppy, { UppyFile } from '@uppy/core';
import type { Restrictions } from '@uppy/core/lib/Restricter';
import DragDropPlugin from '@uppy/drag-drop';
import XHRUpload from '@uppy/xhr-upload';
import type { MultiFileEntry } from './multi-file-uploader-types';
import type {
  UppyMeta,
  UppyBody,
  AttachmentData,
  UploadParamsCallback,
  UploaderRestrictions,
  UniversalMediaUploaderEvent,
} from './universal-media-uploader-types';

export interface UniversalMediaUploaderContext {
  uploaderId: string;
  uppyInstance: Uppy<UppyMeta, UppyBody> | null;
  uploadProgress: number;
  error: Error | null;
  attachmentData: AttachmentData | null;
  restrictions: UploaderRestrictions;
  onUploadParams?: UploadParamsCallback;
  onFinalize?: () => Promise<void>;
  onBackboneSync?: (attachmentData: AttachmentData) => void;
  persist: boolean;
  dragDropTarget?: HTMLElement;
  wasRecentlyUploaded: boolean;
  /** Current total size of already uploaded files for validation (bytes) */
  currentTotalSize: number;
  /** Current count of already uploaded files for validation */
  currentFileCount: number;
  /** Enable multi-file upload mode */
  multiFileMode: boolean;
  /** Map of file IDs to their upload state (for multi-file mode) */
  files: Map<string, MultiFileEntry>;
  /** Maximum number of concurrent uploads */
  maxConcurrentUploads: number;
  /** Array of failed files from batch upload */
  failedFiles: UppyFile<UppyMeta, UppyBody>[];
  /**
   * Maximum image dimension in pixels. When set, images exceeding this bound are
   * resized before upload. Undefined means no compression.
   */
  downscaleTo: number | undefined;
  /**
   * Allow files with duplicate names to be added to the same upload batch.
   * When true, duplicate files are given unique IDs instead of being rejected.
   */
  allowDuplicates: boolean;
}

/**
 * Creates a configured Uppy instance for file uploads.
 *
 * @param uploaderId - Unique identifier for the uploader instance
 * @param restrictions - Configuration options including file restrictions and upload endpoint
 * @returns Configured Uppy instance with XHRUpload plugin if endpoint is provided
 */
function createUppyInstance(
  uploaderId: string,
  restrictions: UploaderRestrictions,
  allowDuplicates: boolean,
): Uppy<UppyMeta, UppyBody> {
  const uppyRestrictions: Partial<Restrictions> = {};

  if (restrictions.maxNumberOfFiles !== undefined) {
    uppyRestrictions.maxNumberOfFiles = restrictions.maxNumberOfFiles;
  }
  if (restrictions.allowedFileTypes !== undefined && restrictions.allowedFileTypes !== null) {
    uppyRestrictions.allowedFileTypes = restrictions.allowedFileTypes;
  }
  if (restrictions.maxFileSize !== undefined) {
    uppyRestrictions.maxFileSize = restrictions.maxFileSize;
  }
  if (restrictions.maxTotalFileSize !== undefined) {
    uppyRestrictions.maxTotalFileSize = restrictions.maxTotalFileSize;
  }

  // Counter scoped per Uppy instance to guarantee unique IDs for duplicate files
  // within the same batch (Date.now() can collide for files processed in the same ms).
  let duplicateCounter = 0;

  const uppy = new Uppy<UppyMeta, UppyBody>({
    id: uploaderId,
    autoProceed: false,
    restrictions: uppyRestrictions,
    ...(allowDuplicates && {
      onBeforeFileAdded: (currentFile, files) => {
        // By default Uppy rejects files with duplicate names. When allowDuplicates
        // is enabled we assign unique IDs so duplicates are accepted.
        if (Object.prototype.hasOwnProperty.call(files, currentFile.id)) {
          return {
            ...currentFile,
            id: `${currentFile.id}-${++duplicateCounter}`,
          };
        }
        return currentFile;
      },
    }),
  });

  // Add XHRUpload plugin if endpoint is provided
  if (restrictions.endpoint) {
    uppy.use(XHRUpload, {
      endpoint: restrictions.endpoint,
      fieldName: 'file',
      formData: true,
      limit: 3, // Maximum 3 concurrent uploads
    });
  }

  return uppy;
}

function destroyUppyInstance(uppy: Uppy<UppyMeta, UppyBody> | null): void {
  if (uppy) {
    try {
      uppy.destroy();
    } catch (error) {
      console.error('Error destroying Uppy instance:', error);
    }
  }
}

/**
 * XState actor that handles post-upload finalization tasks.
 *
 * Invoked after successful upload when `onFinalize` callback is provided.
 * Transitions to `completed` on success or `error` on failure.
 *
 * @param input - State machine context with `onFinalize` callback
 */
export const invokeFinalization = fromPromise(async ({ input }: { input: UniversalMediaUploaderContext }) => {
  if (input.onFinalize) {
    await input.onFinalize();
  }
});

export const universalMediaUploaderMachine = createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFUB2BLAbmATrAhgDYCyk6+yADoQPb4S4DEAkgHLMAqzAggDLMAtAKIBtAAwBdRKEo1Y6AC7oaqaSAAeiABxiAzADpdAJj0A2cwEYArABYxRgOwAaEAE9EFowF8vLtFlwCEjIKajoGHH0AVwwMJSJ0AC9IFnYuPkFRSTVZeSUVNU0EC2MjfQdTK1NdLRsqnQBOKxd3BDqxfSNdJqarBprTByMrHz8MbDwiUghyKlp6XH10CEIwRgAxZl4hAH1uABF9oX3xKSQQXMVlVXOi7oNTLQcrEwbTMTrKlsR+i30LSrVKxaXRWCx2by+ED+CZBaazMILSLLVaMABKQgAykIOKccnIrgVbogTCZOhYtKZ+sZgY9TN8ELobFpymIxFZQVorBzTEYtKNoeNAlMQnNwotMAkIPglKgoOisRw0cwAMJcADyrB2624W2OePOl3yN1ARTsDyeLzEbw+VWabkQVL+DlBDRs-QcDjsNlMAphwuCM1C8wi+klhGWMvQcsYyAACrx1QcdnG0eqAOIYzGYg0yAnGwqIKzOB0IBz2fSmCzW3S6a3F6s2P1CyaBhEhiVSqMx+OJ5NCNFptG5i7566Ftp6SuW17vT721oOJ7ThpiF2PYZVhzNgKt+HB8WRKKImZypaoOM4GhQHBwWCxhNJ-YptOZrE57KGsdE00-IwMxxTH0NkQLER4tGsaod1hEUgzFJFohPaMoHPS9r1vWB717J8dkxZAVRVd8RyNcdiQQIwGhZXkrDZYsGlJZkS1aUEDCGSpKQ5CoGiaaCA33eDQ2PENkNQq8bzvB8+2fPCCKIiwzjzPJSN-BA3hZBw1PeSi+VsLQGQpAwXiZIxATBAEeKhf091FE9FiE8IROjNDxMwyScIHIdiO-E0NB+R5yk060tB05kGV0zo+hqIYjGZEzeOsuDbKPJCzwAM2jBJEmQxgIBUMBz0wGgAGt8qsuEbI7ZLhLSjKIyyuUEGjQqAGMoxUU4vKUn9fNU-yNLpIKQr00tazKGo+XdJkql0Kl4vKxLKsQ6qUPS1BMuy3Ar0iagZVSmgcAAW30MrYPbQ8locmq1rq5DGtQFq2tQDrP0UwkfKKNSAoG7SuVC0s6mdLRV09MDq1MZlfUslt5rOhDmpoA7qDABQUgxYh1QANV2TZtk6t6J3BAF9Eix5mT6BwKSYklq30RpVwaEoq10Cxtyh3cYYPOGEaRlGIAVbFcRe0cuvejwbCJknai5BoKaeBk6yAio61rGxxb5J45tOznQ02-bGBVbZuDRHYPPVYchZI7qik8Z59B9J4geqcWmhsMKfX+eiKco7j7BsXRNbbbXFl1nB+ZxPGCzIm2rDtx4lzeJkLBdhlwbKcFgVVkxxYg-2BVQGgGHgc4TsDgTcHxEWJwAWld0s3mAro-eMEpa1rEY2Zg0ukuiWIrkyyAK-xqOKQ6JpmaMIxrDrH0GRsF1Y63axppBEoA-47uUTAQfI5UpegNdb1ujELlmdn91Kx5d4AXZJP+Q7viKvO8NI1lKBt+UnqTIeYZaI0hjhsXMMWmuhniOAggCT0ucxjsy1mXKql035fkrmRVW+ktAsndNxHQNQZYmA+GvR+CF7L0EcheMSGEi6vR3j1TwDQ7aT3QbWKsTQNIAMQCAoCY9PCYNrC3AhC1zrENPCtWqSRkLvyto6fonRTLM3LGuRw8sOR2w5CUDS3IOGUn4bDUM8NEarF5hI0WxQdKdD0IzP2FQKZU3InYFRzMmSVBlmICwFhtFB0iCHIxBMKQxy4RPKeHx6SlmLDHEEfQ6g2AnpFVmPggA */
  id: 'UniversalMediaUploader',

  types: {} as {
    context: UniversalMediaUploaderContext;
    events: UniversalMediaUploaderEvent;
  },

  context: ({ input }: { input: { uploaderId: string } }) => ({
    uploaderId: input.uploaderId,
    uppyInstance: null,
    uploadProgress: 0,
    error: null,
    attachmentData: null,
    restrictions: {},
    onUploadParams: undefined,
    onFinalize: undefined,
    onBackboneSync: undefined,
    persist: false,
    dragDropTarget: undefined,
    wasRecentlyUploaded: false,
    currentTotalSize: 0,
    currentFileCount: 0,
    multiFileMode: false,
    files: new Map(),
    maxConcurrentUploads: 3,
    failedFiles: [],
    downscaleTo: undefined,
    allowDuplicates: false,
  }),

  initial: 'uninitialized',

  states: {
    uninitialized: {
      on: {
        INITIALIZE: {
          target: 'idle',
          actions: ['initializeContext', 'createUppy'],
        },
      },
    },

    idle: {
      on: {
        FILE_ADDED: {
          target: 'validating',
          actions: 'assignFile',
        },
        RESTRICTION_FAILED: [
          {
            // In multi-file mode, add the rejected file to the files map with its
            // error so it appears in uploadErrors and renders in the modal list.
            guard: 'hasFileInMultiFileMode',
            actions: 'assignRestrictionErrorToFile',
          },
          {
            // Multi-file mode without a file reference — nothing to display.
            guard: 'isMultiFileMode',
          },
          {
            target: 'error',
            actions: 'assignError',
          },
        ],
        UPLOAD_COMPLETE: {
          target: 'completed',
          guard: 'isMultiFileMode',
          actions: ['processMultiFileBatchCompletion', 'callBatchFinalization', 'markAsRecentlyUploaded'],
        },
        FILE_REMOVED: [
          {
            guard: 'isMultiFileMode',
            actions: 'removeSingleFile',
            description: 'Remove single error file in multi-file mode',
          },
          {
            actions: 'clearState',
          },
        ],
        INITIALIZE: {
          actions: ['cleanupUppy', 'updateConfiguration', 'createUppy'],
        },
        RESET: {
          actions: 'clearState',
        },
      },
    },

    validating: {
      on: {
        FILE_ADDED: {
          guard: 'isMultiFileMode',
          actions: 'assignFile',
          description: 'Add additional file in multi-file mode during validation',
        },
        RESTRICTION_FAILED: [
          {
            guard: 'hasFileInMultiFileMode',
            actions: 'assignRestrictionErrorToFile',
          },
          {
            guard: 'isMultiFileMode',
          },
          {
            target: 'error',
            actions: 'assignError',
          },
        ],
        UPLOAD_PROGRESS: {
          target: 'uploading',
          actions: 'assignProgress',
        },
        FILE_SIZE_UPDATED: {
          actions: 'updateFileSize',
          description: 'Update file size in state when image was resized before upload',
        },
        UPLOAD_ERROR: [
          {
            target: 'error',
            guard: ({ context }) => !context.multiFileMode,
            actions: 'markFileError',
            description: 'Single-file mode: transition to error state',
          },
          {
            actions: 'markFileError',
            description: 'Multi-file mode: mark individual file with error, stay in validating',
          },
        ],
        // Handle UPLOAD_SUCCESS directly from validating state for fast uploads
        // that complete before any progress events are received.
        UPLOAD_SUCCESS: [
          {
            target: '#UniversalMediaUploader.uploading.finalizing',
            guard: 'hasFinalizationCallback',
            actions: 'assignAttachment',
          },
          {
            guard: 'isMultiFileMode',
            actions: 'assignAttachment',
          },
          {
            target: '#UniversalMediaUploader.completed',
            actions: ['assignAttachment', 'callBackboneSync', 'markAsRecentlyUploaded'],
          },
        ],
        FILE_REMOVED: [
          {
            guard: 'isLastFileInMultiFileMode',
            target: 'idle',
            actions: 'removeSingleFile',
            description: 'Last file removed in multi-file mode during validation — return to idle',
          },
          {
            guard: 'isMultiFileMode',
            actions: 'removeSingleFile',
            description: 'Remove single file in multi-file mode during validation',
          },
          {
            target: 'idle',
            actions: 'clearState',
          },
        ],
        UPLOAD_COMPLETE: {
          target: 'completed',
          guard: 'isMultiFileMode',
          actions: ['processMultiFileBatchCompletion', 'callBatchFinalization', 'markAsRecentlyUploaded'],
          description: 'All files in batch completed while still validating',
        },
        CANCEL_ALL: {
          target: 'idle',
          actions: 'clearState',
        },
      },
    },

    uploading: {
      initial: 'inProgress',
      states: {
        inProgress: {
          on: {
            UPLOAD_PROGRESS: {
              actions: 'assignProgress',
            },
            FILE_SIZE_UPDATED: {
              actions: 'updateFileSize',
              description: 'Update file size in state when image was resized before upload',
            },
            UPLOAD_SUCCESS: [
              {
                target: 'finalizing',
                guard: 'hasFinalizationCallback',
                actions: 'assignAttachment',
              },
              {
                guard: 'isMultiFileMode',
                actions: 'assignAttachment',
              },
              {
                target: '#UniversalMediaUploader.completed',
                actions: ['assignAttachment', 'callBackboneSync', 'markAsRecentlyUploaded'],
              },
            ],
            UPLOAD_ERROR: [
              {
                target: '#UniversalMediaUploader.error',
                guard: ({ context }) => !context.multiFileMode,
                actions: 'markFileError',
              },
              {
                actions: 'markFileError',
              },
            ],
            RESTRICTION_FAILED: [
              {
                guard: 'hasFileInMultiFileMode',
                actions: 'assignRestrictionErrorToFile',
              },
              {
                guard: 'isMultiFileMode',
              },
            ],
            UPLOAD_COMPLETE: {
              target: '#UniversalMediaUploader.completed',
              guard: 'isMultiFileMode',
              actions: ['processMultiFileBatchCompletion', 'callBatchFinalization', 'markAsRecentlyUploaded'],
              description: 'All files in batch have completed uploading',
            },
            FILE_REMOVED: [
              {
                guard: 'isLastFileInMultiFileMode',
                target: '#UniversalMediaUploader.idle',
                actions: 'removeSingleFile',
                description: 'Last file removed in multi-file mode during upload — return to idle',
              },
              {
                guard: 'isMultiFileMode',
                actions: 'removeSingleFile',
                description: 'Remove single file (e.g. dismiss error) without disrupting other uploads',
              },
              {
                target: '#UniversalMediaUploader.idle',
                actions: 'clearState',
              },
            ],
            CANCEL_ALL: {
              target: '#UniversalMediaUploader.idle',
              actions: 'clearState',
            },
          },
        },

        finalizing: {
          invoke: {
            src: 'invokeFinalization',
            input: ({ context }) => context,
            onDone: {
              target: '#UniversalMediaUploader.completed',
              actions: ['callBackboneSync', 'markAsRecentlyUploaded'],
            },
            onError: {
              target: '#UniversalMediaUploader.error',
              actions: 'assignFinalizationError',
            },
          },
        },
      },
    },

    completed: {
      on: {
        FILE_ADDED: {
          guard: 'isMultiFileMode',
          target: 'validating',
          actions: ['assignFile', 'validateFiles'],
          description: 'Add file in multi-file mode when already completed',
        },
        RESTRICTION_FAILED: [
          {
            guard: 'hasFileInMultiFileMode',
            actions: 'assignRestrictionErrorToFile',
          },
          {
            guard: 'isMultiFileMode',
          },
        ],
        FILE_REMOVED: [
          {
            guard: 'isMultiFileMode',
            actions: 'removeSingleFile',
            description: 'Remove single file in multi-file mode',
          },
          {
            target: 'idle',
            actions: 'clearState',
            description: 'Clear all in single-file mode',
          },
        ],
        REMOVE_FILE: {
          target: 'idle',
          actions: 'clearState',
        },
        INITIALIZE: {
          actions: ['cleanupUppy', 'updateConfiguration', 'createUppy'],
        },
        RESET: {
          target: 'idle',
          actions: 'clearState',
        },
      },
    },

    error: {
      on: {
        CLEAR_ERROR: {
          target: 'idle',
          actions: 'clearError',
        },
        INITIALIZE: {
          actions: ['cleanupUppy', 'updateConfiguration', 'createUppy'],
        },
        RESET: {
          target: 'idle',
          actions: 'clearState',
        },
      },
    },
  },
}, {
  actions: {
    initializeContext: assign({
      restrictions: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.restrictions || {};
      },
      onUploadParams: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.onUploadParams;
      },
      onFinalize: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.onFinalize;
      },
      onBackboneSync: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.onBackboneSync;
      },
      persist: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.persist ?? false;
      },
      dragDropTarget: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.dragDropTarget;
      },
      currentTotalSize: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.currentTotalSize ?? 0;
      },
      currentFileCount: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.currentFileCount ?? 0;
      },
      multiFileMode: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.multiFile ?? false;
      },
      downscaleTo: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.downscaleTo;
      },
      allowDuplicates: ({ event }) => {
        assertEvent(event, 'INITIALIZE');
        return event.allowDuplicates ?? false;
      },
    }),

    updateConfiguration: assign({
      restrictions: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.restrictions ?? context.restrictions;
      },
      onUploadParams: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.onUploadParams ?? context.onUploadParams;
      },
      onFinalize: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.onFinalize ?? context.onFinalize;
      },
      onBackboneSync: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.onBackboneSync ?? context.onBackboneSync;
      },
      persist: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.persist ?? context.persist;
      },
      dragDropTarget: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.dragDropTarget ?? context.dragDropTarget;
      },
      currentTotalSize: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.currentTotalSize ?? context.currentTotalSize;
      },
      currentFileCount: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.currentFileCount ?? context.currentFileCount;
      },
      multiFileMode: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.multiFile ?? context.multiFileMode;
      },
      downscaleTo: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.downscaleTo ?? context.downscaleTo;
      },
      allowDuplicates: ({ event, context }) => {
        assertEvent(event, 'INITIALIZE');
        return event.allowDuplicates ?? context.allowDuplicates;
      },
    }),

    createUppy: assign({
      uppyInstance: ({ context }) => {
        // Subtract existing files/size so Uppy only allows room for new ones
        const adjustedRestrictions: UploaderRestrictions = {
          ...context.restrictions,
          ...(context.restrictions.maxNumberOfFiles !== undefined && {
            maxNumberOfFiles: Math.max(0, context.restrictions.maxNumberOfFiles - context.currentFileCount),
          }),
          ...(context.restrictions.maxTotalFileSize !== undefined && {
            maxTotalFileSize: Math.max(0, context.restrictions.maxTotalFileSize - context.currentTotalSize),
          }),
        };

        const uppy = createUppyInstance(
          context.uploaderId,
          adjustedRestrictions,
          context.allowDuplicates,
        );

        // Add DragDrop plugin if target is provided
        if (context.dragDropTarget) {
          uppy.use(DragDropPlugin, {
            target: context.dragDropTarget,
          });
        }

        return uppy;
      },
    }),

    cleanupUppy: ({ context }) => {
      destroyUppyInstance(context.uppyInstance);
    },

    assignFile: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'FILE_ADDED');
        const newFiles = new Map(context.files);

        // In single-file mode, clear existing files first
        if (!context.multiFileMode) {
          newFiles.clear();
        }

        // Add the new file with initial state
        newFiles.set(event.file.id, {
          file: event.file,
          progress: 0,
          attachmentData: undefined,
          error: undefined,
        });

        return newFiles;
      },
      uploadProgress: () => 0,
      wasRecentlyUploaded: () => false,
    }),

    assignProgress: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'UPLOAD_PROGRESS');
        const newFiles = new Map(context.files);
        const fileId = event.file.id;
        const existingEntry = newFiles.get(fileId);

        const { bytesUploaded, bytesTotal } = event.progress;
        const progress = bytesTotal > 0 ? (bytesUploaded / bytesTotal) * 100 : 0;

        if (existingEntry) {
          newFiles.set(fileId, {
            ...existingEntry,
            progress,
          });
        } else {
          // File wasn't in map yet (shouldn't happen, but defensive)
          newFiles.set(fileId, {
            file: event.file,
            progress,
          });
        }

        return newFiles;
      },
      uploadProgress: ({ context, event }) => {
        assertEvent(event, 'UPLOAD_PROGRESS');
        // In single-file mode, update global progress for backward compatibility
        if (!context.multiFileMode) {
          const { bytesUploaded, bytesTotal } = event.progress;
          return bytesTotal > 0 ? (bytesUploaded / bytesTotal) * 100 : 0;
        }
        return context.uploadProgress;
      },
    }),

    assignAttachment: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'UPLOAD_SUCCESS');
        const newFiles = new Map(context.files);
        const fileId = event.file.id;
        const existingEntry = newFiles.get(fileId);

        const responseData = ('body' in event.response ? event.response.body : event.response) as AttachmentData;

        // Generate blob URL from file data for preview if no server URL available
        const previewUrl = responseData?.url
          || (event.file.data ? URL.createObjectURL(event.file.data as Blob) : undefined);

        const attachmentData = event.attachmentId
          ? {
            ...responseData,
            id: event.attachmentId,
            name: event.file.name,
            size: event.file.size ?? undefined,
            url: previewUrl,
          }
          : {
            ...responseData,
            url: responseData?.url || previewUrl,
          };

        if (existingEntry) {
          newFiles.set(fileId, {
            ...existingEntry,
            progress: 100,
            attachmentData,
          });
        }

        return newFiles;
      },
      attachmentData: ({ context, event }) => {
        assertEvent(event, 'UPLOAD_SUCCESS');
        // In single-file mode, maintain backward compatibility
        if (!context.multiFileMode) {
          const responseData = ('body' in event.response ? event.response.body : event.response) as AttachmentData;

          // Generate blob URL from file data for preview if no server URL available
          const previewUrl = responseData?.url
            || (event.file.data ? URL.createObjectURL(event.file.data as Blob) : undefined);

          return event.attachmentId
            ? {
              ...responseData,
              id: event.attachmentId,
              name: event.file.name,
              size: event.file.size ?? undefined,
              url: previewUrl,
            }
            : {
              ...responseData,
              url: responseData?.url || previewUrl,
            };
        }
        return context.attachmentData;
      },
    }),

    assignRestrictionErrorToFile: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'RESTRICTION_FAILED');
        if (!event.file) return context.files;
        const newFiles = new Map(context.files);
        newFiles.set(event.file.id, {
          file: event.file,
          progress: 0,
          error: event.error,
        });
        return newFiles;
      },
    }),

    assignError: assign({
      error: ({ event }) => {
        assertEvent(event, ['UPLOAD_ERROR', 'RESTRICTION_FAILED']);
        return event.error;
      },
    }),

    assignFinalizationError: assign({
      error: ({ event }) => {
        if ('error' in event) {
          return event.error instanceof Error ? event.error : new Error(String(event.error));
        }
        return new Error('Finalization failed');
      },
    }),

    markAsRecentlyUploaded: assign({
      wasRecentlyUploaded: () => true,
    }),

    callBackboneSync: ({ context }) => {
      if (context.onBackboneSync && context.attachmentData) {
        let result;
        try {
          result = context.onBackboneSync(context.attachmentData);
        } catch (error) {
          console.error('Error calling onBackboneSync:', error);
          return;
        }
        Promise.resolve(result).catch((error) => {
          console.error('Error calling onBackboneSync:', error);
        });
      }
    },

    clearError: assign({
      error: () => null,
      files: () => new Map(),
      uploadProgress: () => 0,
      attachmentData: () => null,
      wasRecentlyUploaded: () => false,
    }),

    clearState: assign({
      files: () => new Map(),
      uploadProgress: () => 0,
      error: () => null,
      attachmentData: () => null,
      wasRecentlyUploaded: () => false,
      failedFiles: () => [],
    }),

    removeSingleFile: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'FILE_REMOVED');
        const fileId = event.file?.id;

        if (!fileId) {
          return context.files;
        }

        const newFiles = new Map(context.files);
        newFiles.delete(fileId);

        return newFiles;
      },
    }),

    markFileError: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'UPLOAD_ERROR');
        const newFiles = new Map(context.files);
        const fileId = event.file?.id;

        if (fileId) {
          const existingEntry = newFiles.get(fileId);
          if (existingEntry) {
            newFiles.set(fileId, {
              ...existingEntry,
              error: event.error,
            });
          }
        }

        return newFiles;
      },
      error: ({ context, event }) => {
        assertEvent(event, 'UPLOAD_ERROR');
        // In single-file mode, set global error for backward compatibility
        if (!context.multiFileMode) {
          return event.error;
        }
        return context.error;
      },
    }),

    processMultiFileBatchCompletion: assign({
      failedFiles: ({ event }) => {
        assertEvent(event, 'UPLOAD_COMPLETE');
        return event.result.failed || [];
      },
    }),

    updateFileSize: assign({
      files: ({ context, event }) => {
        assertEvent(event, 'FILE_SIZE_UPDATED');
        const newFiles = new Map(context.files);
        const fileId = event.file.id;
        const existingEntry = newFiles.get(fileId);

        if (existingEntry) {
          // Replace the entire file object so both data (resized blob) and size
          // reflect the actual upload payload rather than the pre-resize original.
          newFiles.set(fileId, {
            ...existingEntry,
            file: event.file,
          });
        }

        return newFiles;
      },
    }),

    callBatchFinalization: ({ context }) => {
      if (!context.multiFileMode || !context.onFinalize) {
        return;
      }

      // Call user's finalization callback
      // Note: The hook layer (use-attachment-uploader) handles attachmentIds collection
      // and formAttachmentIds management
      // We use an IIFE with Promise.resolve to catch both sync and async errors
      // while still calling the callback immediately (not deferred to microtask)
      (async () => {
        await context.onFinalize!();
      })().catch((error) => {
        console.error('[StateMachine] Error calling batch finalization:', error);
      });
    },
  },

  guards: {
    isMultiFileMode: ({ context }) => context.multiFileMode,
    hasFileInMultiFileMode: ({ context, event }) => {
      if (!context.multiFileMode) return false;
      if ('file' in event && event.file != null) return true;
      return false;
    },
    hasFinalizationCallback: ({ context }) => typeof context.onFinalize === 'function' && !context.multiFileMode,
    isLastFileInMultiFileMode: ({ context, event }) => {
      if (!context.multiFileMode) return false;
      const fileId = 'file' in event ? (event.file as { id?: string })?.id : undefined;
      if (!fileId) return context.files.size <= 1;
      // Check if removing this file would leave the map empty
      return context.files.size <= 1 && context.files.has(fileId);
    },
  },

  actors: {
    invokeFinalization,
  },
});
